import { BenqiSavaxAbi } from '../abis/benqi'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * BENQI sAVAX reader.
 *
 * Three calls:
 *   1. `sAVAX.totalSupply()`
 *   2. `sAVAX.getPooledAvaxByShares(1e18)` — 1e18-scaled AVAX-per-sAVAX
 *   3. `sAVAX.totalPooledAvax()` — total AVAX held
 *
 * Lido-clone pattern. Function names per BENQI docs — the live
 * read on chainId 43114 confirms them (entries silently drop on
 * mismatch thanks to the `'0x'`-safe `toBigInt`).
 */
export const readerBenqiSavax = (entry: LstRegistryEntry): LstReader => ({
  calls: [
    { address: entry.address, name: 'totalSupply', params: [] },
    {
      address: entry.address,
      name: 'getPooledAvaxByShares',
      params: [ONE_E18],
    },
    { address: entry.address, name: 'totalPooledAvax', params: [] },
  ],
  abis: [TotalSupplyAbi, BenqiSavaxAbi, BenqiSavaxAbi],
  parse: ([supply, rate, totalPooled]) => {
    const totalSupply = toBigInt(supply)
    const exchangeRate = toBigInt(rate)
    if (totalSupply === undefined || exchangeRate === undefined) {
      return undefined
    }
    // Prefer the protocol's own totalPooledAvax (consistent with the
    // rate's reporting); fall back to derivation.
    const totalAssets =
      toBigInt(totalPooled) ?? (totalSupply * exchangeRate) / ONE_E18
    return {
      totalAssets,
      totalSupply,
      exchangeRate,
    }
  },
})
